import type { SimplePoint } from "@ptx-autonomous/libs/api-models";
import area from "@turf/area";
import { lineString } from "@turf/helpers";
import length from "@turf/length";
import type { Polygon } from "geojson";
import type { PreMissionMetrics } from "../types";
import { Speed } from "@ptx-autonomous/libs/units";

// HL_SWATH points (used for transition between work areas) are ignored in the current implementation
export function usePreMissionMetrics(
	fieldBoundary: Polygon,
	workAreas: Polygon[],
	plannedPath: SimplePoint[],
	targetSpeedKph: number,
	turningSpeedKph: number,
): PreMissionMetrics {
	interface Segment {
		points: SimplePoint[];
		length: number;
	}

	const segments: {
		SWATH: Segment[];
		TURN: Segment[];
	} = {
		SWATH: [],
		TURN: [],
	};

	const targetSpeedMph = new Speed(targetSpeedKph, "kph").convert("mph");
	const turningSpeedMph = new Speed(turningSpeedKph, "kph").convert("mph");

	let currentSegment: SimplePoint[] = [];
	let currentType: "SWATH" | "TURN" | null = null;
	let lastPointOfPreviousSegment: SimplePoint | null = null;

	function addSegmentToTotal(
		segment: SimplePoint[],
		type: "SWATH" | "TURN",
	): void {
		if (segment.length >= 2) {
			const pointsToUse = lastPointOfPreviousSegment
				? [lastPointOfPreviousSegment, ...segment]
				: segment;

			const line = lineString(
				pointsToUse.map(point => [point.Long, point.Lat]),
			);
			segments[type].push({
				points: segment,
				length: length(line, { units: "miles" }),
			});
		}
	}

	plannedPath.forEach((point, index) => {
		if (point.Label === "SWATH" && currentType !== "SWATH") {
			if (currentSegment.length > 0) {
				addSegmentToTotal(currentSegment, currentType || "TURN");
				lastPointOfPreviousSegment =
					currentSegment[currentSegment.length - 1];
			}
			currentSegment = [point];
			currentType = "SWATH";
		} else if (point.Label === "TURN" && currentType !== "TURN") {
			if (currentSegment.length > 0) {
				addSegmentToTotal(currentSegment, currentType || "SWATH");
				lastPointOfPreviousSegment =
					currentSegment[currentSegment.length - 1];
			}
			currentSegment = [point];
			currentType = "TURN";
		} else {
			currentSegment.push(point);
		}

		if (index === plannedPath.length - 1 && currentSegment.length > 0) {
			addSegmentToTotal(currentSegment, currentType || "TURN");
		}
	});

	return {
		fieldArea: area({
			type: "Polygon",
			coordinates: fieldBoundary.coordinates,
		}),
		workAreaArea: area({
			type: "MultiPolygon",
			coordinates: workAreas.map(wa => wa.coordinates),
		}),
		pathLength: length(
			lineString(plannedPath.map(point => [point.Long, point.Lat])),
			{ units: "miles" },
		),
		swathLength: segments.SWATH.reduce(
			(sum, segment) => sum + segment.length,
			0,
		),
		turnLength: segments.TURN.reduce(
			(sum, segment) => sum + segment.length,
			0,
		),
		get efficiencyRatio() {
			const totalDistance = this.swathLength + this.turnLength;
			return totalDistance > 0 ? this.swathLength / totalDistance : 0;
		},
		get swathTime() {
			return this.swathLength / (targetSpeedMph / 3600);
		},
		get turnTime() {
			return this.turnLength / (turningSpeedMph / 3600);
		},
		get totalTime() {
			return (
				this.swathLength / (targetSpeedMph / 3600) +
				this.turnLength / (turningSpeedMph / 3600)
			);
		},
	};
}
